'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'

interface YouTubeEmbedProps {
  videoId: string
  title?: string
}

const YouTubeEmbed = ({
  videoId,
  title = 'YouTube video player',
}: YouTubeEmbedProps) => {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const currentRef = ref.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true)
          observer.disconnect()
        }
      },
      {
        rootMargin: '50px',
      },
    )

    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [])

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`

  return (
    <div
      ref={ref}
      className="relative my-6 overflow-hidden rounded-lg shadow-lg"
      style={{ paddingTop: '56.25%' /* 16:9 Aspect Ratio */ }}
    >
      {isIntersecting ? (
        <iframe
          className="absolute top-0 left-0 h-full w-full"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <div className="absolute top-0 left-0 h-full w-full group">
          <Image
            src={thumbnailUrl}
            alt={title || 'YouTube video thumbnail'}
            fill
            className="object-cover"
            unoptimized
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 transition-colors group-hover:bg-opacity-50">
            <svg
              className="h-16 w-16 text-white opacity-80 transition-transform group-hover:scale-110"
              viewBox="0 0 68 48"
              fill="currentColor"
              aria-label="YouTube play button"
              role="img"
            >
              <path
                d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                fill="#f00"
              />
              <path d="M 45,24 27,14 27,34" fill="#fff" />
            </svg>
          </div>
        </div>
      )}
    </div>
  )
}

export default YouTubeEmbed
